import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { luhnCheck } from './credit-card';

export class CustomValidators {

  /**
   * Uses when two inputs in form group should have identical values (password confirmation for example)
   *
   * @param controlName
   * @param matchingControlName
   */
  public static mustMatch(controlName: string, matchingControlName: string) {
    return (formGroup: FormGroup) => {
      const control = formGroup.controls[controlName];
      const matchingControl = formGroup.controls[matchingControlName];

      // set error on matchingControl if validation fails
      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ must_match: true });
      } else {
        matchingControl.setErrors(null);
      }
    };
  }

  /**
   * Uses for override error key that uses native validators
   *
   * Example changeKey('email_pattern', Validators.pattern(<email validation regexp>))
   *  result: {email_pattern: true}
   *
   * If you will just use Validators.pattern(<email validation regexp>)
   *  result should be: {pattern: true}
   *
   * @param newKey
   * @param validator
   */
  public static changeKey(newKey: string, validator: ValidatorFn): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      const invalid = validator(control);

      if (invalid) {
        const validationResult = invalid[Object.keys(invalid)[0]];

        return {
          [newKey]: validationResult
        };
      } else {
        return null;
      }
    };
  }

  /**
   * Is user eighteen or more years old
   *
   * @param control
   */
  // @ts-ignore
  public static eighteenYearsOld(control: AbstractControl): {[key: string]: any} | null {
    if (!control.value) {
      return null;
    }
    const [day, month, year] = control.value.split('/');
    if (!day && !month && !year) {
      return null;
    }
    const birthDate = new Date(year, month - 1, day);
    const currentDate = new Date();
    const diff = currentDate.getTime() - birthDate.getTime();
    const age = Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
    if (age < 18) {
      return { eighteen_years: true };
    } else {
      return null;
    }
  }

  // @ts-ignore
  public static validDate(control: AbstractControl): {[key: string]: any} | null {
    if (control.value) {
      const days = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
      const [yy, mm, dd] = control.value.split(/[\/\-]/).map((val: any) => Number(val || 0));

      const invalid = mm && dd && dd > days[mm - 1];

      if (invalid) {
        return {
          pattern: invalid
        };
      } else {
        return null;
      }
    }
  }

  /**
   * The function luhnValidator checks if a given value passes the Luhn algorithm validation.
   * @param {AbstractControl} control - The `control` parameter in the `luhnValidator` function is of
   * type `AbstractControl`. This parameter is used to access the value that needs to be validated using
   * the Luhn algorithm. The function checks if the control value is valid according to the Luhn
   * algorithm and returns an object with a
   * @returns The `luhnValidator` function returns an object with a key-value pair where the key is
   * "pattern" and the value is the result of the validation from the `luhnCheck` function. If the
   * validation is successful, it returns `null`.
   */
  public static luhnValidator(control: AbstractControl): {[key: string]: any} | null {
    if (control.value) {
      const invalid = !luhnCheck(control.value);
      if (invalid) {
        const validationResult = invalid[Object.keys(invalid)[0]];
        return {
          pattern: validationResult
        };
      } else {
        return null;
      }
    }
  }

  public static expiryDateValidator(control: AbstractControl): ValidationErrors | null {
    if (!control?.value) {
      return null;
    }
    const today = new Date();
    const [inputMonth, inputYear] = control?.value?.split('/')?.map(Number);
    const currentMonth = today?.getMonth() + 1;
    const currentYear = today?.getFullYear() % 100; // Last two digits of the year
    if (inputYear < currentYear) {
      return {
        invalid_year: true
      };
    }
    if (inputYear === currentYear && inputMonth < currentMonth) {
      return {
        invalid_month: true
      };
    }
    if (inputMonth > 12 || inputMonth < 1) {
      return {
        invalid_month_format: true
      };
    }
    return null;
  }

  /**
   * The `decemicalLimit` function is a custom validator that ensures the input value is a valid decimal number with a maximum of two decimal places.
   * @param control - The `AbstractControl` instance that needs to be validated.
   * @returns `null` if the input value is valid, or an object with a `pattern` property if the input value is invalid.
   */
  public static decemicalLimit(control: AbstractControl): ValidationErrors | null {
    if (!control?.value) {
      return null;
    }
    const value = control.value;
    const regex = /^\d+(\.\d{0,2})?$/;
    if (!regex.test(value)) {
      control.setValue(parseFloat(value).toFixed(2), { emitEvent: false });
    }
    return null;
  }

  /**
   * The `passwordValidator` function is a custom validator that ensures the input password meets certain requirements.
   * @param control - The `AbstractControl` instance that needs to be validated.
   * @returns `null` if the input password is valid, or an object with `password_length`, `password_en_pattern`, and `password_pattern` properties if the input password is invalid.
   */
  public static passwordValidator(control: AbstractControl): ValidationErrors | null {
    if (!control?.value) {
      return null;
    }
    const password: string = control.value || '';
    const errors: ValidationErrors = {};

    if (password.length < 8) {
      errors.password_length = true;
    }

    if (password.length > 64) {
      errors.maxlength = true;
    }

    const allowedSymbols = "@#$%^&+()=!~'<>,.{}[]";
    const allowedSymbolsArray = allowedSymbols.split('');

    const invalidLetters: string[] = [];
    const invalidSymbols: string[] = [];

    for (const char of password) {
      if (/[A-Za-z0-9]/.test(char)) {
        continue;
      }

      if (allowedSymbolsArray.includes(char)) {
        continue;
      }

      if (/[\p{L}]/u.test(char) && !/[A-Za-z]/.test(char)) {
        invalidLetters.push(char);
      } else {
        invalidSymbols.push(char);
      }
    }

    if (invalidLetters.length > 0) {
      errors.password_en_pattern = Array.from(new Set(invalidLetters)).join(', ');
    }

    if (invalidSymbols.length > 0) {
      errors.password_pattern = Array.from(new Set(invalidSymbols)).join(', ');
    }

    return Object.keys(errors).length ? errors : null;
  }

}

